import React, { useState } from 'react';

type AlertType = {
  message: string;
  type: 'error' | 'success';
} | null;

interface ChangePasswordProps {
  onBack: () => void;
  onSuccess: () => void;
}

const ChangePassword: React.FC<ChangePasswordProps> = ({ onBack, onSuccess }) => {
  const [step, setStep] = useState<'email' | 'code' | 'newPassword'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState<AlertType>(null);

  const showAlert = (message: string, type: 'error' | 'success') => {
    setAlert({ message, type });
    setTimeout(() => setAlert(null), 4000);
  };

  const handleSendCode = () => {
    if (!email) {
      showAlert('Inserisci la tua email', 'error');
      return;
    }

    setLoading(true);
    setTimeout(() => {
      showAlert('Codice di verifica inviato alla tua email!', 'success');
      setStep('code');
      setLoading(false);
    }, 1500);
  };

  const handleVerifyCode = () => {
    if (!code) {
      showAlert('Inserisci il codice di verifica', 'error');
      return;
    }

    setLoading(true);
    setTimeout(() => {
      if (code === '123456') {
        showAlert('Codice verificato con successo!', 'success');
        setStep('newPassword');
        setLoading(false);
      } else {
        showAlert('Codice non valido. Usa 123456 per il test', 'error');
        setLoading(false);
      }
    }, 1000);
  };

  const handleChangePassword = () => {
    if (!newPassword || !confirmPassword) {
      showAlert('Compila tutti i campi', 'error');
      return;
    }

    if (newPassword.length < 6) {
      showAlert('La password deve essere di almeno 6 caratteri', 'error');
      return;
    }

    if (newPassword !== confirmPassword) {
      showAlert('Le password non coincidono', 'error');
      return;
    }

    setLoading(true);
    setTimeout(() => {
      showAlert('Password cambiata con successo!', 'success');
      setTimeout(() => {
        onSuccess();
      }, 1500);
    }, 1000);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-5" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          <div className="p-10 text-center text-white" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            <h1 className="text-3xl font-bold mb-2 flex items-center justify-center gap-2">
              <span>🔐</span> Cambio Password
            </h1>
            <p className="opacity-90">
              {step === 'email' && 'Inserisci la tua email'}
              {step === 'code' && 'Verifica il codice ricevuto'}
              {step === 'newPassword' && 'Imposta la nuova password'}
            </p>
          </div>

          <div className="p-8">
            {alert && (
              <div className={`p-3 rounded-lg mb-5 flex items-center gap-2 ${alert.type === 'error' ? 'bg-red-50 border border-red-200 text-red-600' : 'bg-green-50 border border-green-200 text-green-600'}`}>
                <span>{alert.type === 'error' ? '❌' : '✅'}</span>
                <span>{alert.message}</span>
              </div>
            )}

            {/* Step 1: Email */}
            {step === 'email' && (
              <div>
                <div className="mb-5">
                  <label className="block mb-2 font-medium text-gray-600 text-sm">📧 Email</label>
                  <div className="relative">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="nome@esempio.it"
                      className="w-full p-3 pl-12 border-2 border-gray-200 rounded-lg bg-gray-50 focus:border-purple-500 focus:bg-white focus:outline-none transition-all"
                    />
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">@</span>
                  </div>
                </div>

                <button
                  onClick={handleSendCode}
                  disabled={loading}
                  className="w-full p-4 rounded-lg text-white font-semibold transition-all hover:shadow-lg disabled:opacity-60"
                  style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="animate-spin">⏳</span> Invio in corso...
                    </span>
                  ) : (
                    '📨 Invia Codice di Verifica'
                  )}
                </button>

                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800">
                  💡 <strong>Per il test:</strong> Inserisci qualsiasi email valida
                </div>
              </div>
            )}

            {/* Step 2: Codice Verifica */}
            {step === 'code' && (
              <div>
                <div className="mb-2 text-sm text-gray-600 text-center">
                  Codice inviato a: <strong>{email}</strong>
                </div>

                <div className="mb-5">
                  <label className="block mb-2 font-medium text-gray-600 text-sm">🔢 Codice di Verifica</label>
                  <input
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="Inserisci il codice a 6 cifre"
                    maxLength={6}
                    className="w-full p-3 text-center text-2xl tracking-widest border-2 border-gray-200 rounded-lg bg-gray-50 focus:border-purple-500 focus:bg-white focus:outline-none transition-all"
                  />
                </div>

                <button
                  onClick={handleVerifyCode}
                  disabled={loading}
                  className="w-full p-4 rounded-lg text-white font-semibold transition-all hover:shadow-lg disabled:opacity-60 mb-3"
                  style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="animate-spin">⏳</span> Verifica in corso...
                    </span>
                  ) : (
                    '✓ Verifica Codice'
                  )}
                </button>

                <button
                  onClick={() => setStep('email')}
                  className="w-full p-3 text-purple-600 hover:text-purple-800 font-medium text-sm"
                >
                  ← Cambia email
                </button>

                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800">
                  💡 <strong>Per il test:</strong> Usa il codice <strong>123456</strong>
                </div>
              </div>
            )}

            {/* Step 3: Nuova Password */}
            {step === 'newPassword' && (
              <div>
                <div className="mb-5">
                  <label className="block mb-2 font-medium text-gray-600 text-sm">🔒 Nuova Password</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Minimo 6 caratteri"
                      className="w-full p-3 pl-12 pr-12 border-2 border-gray-200 rounded-lg bg-gray-50 focus:border-purple-500 focus:bg-white focus:outline-none transition-all"
                    />
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">🔑</span>
                    <span 
                      className="absolute right-4 top-1/2 -translate-y-1/2 cursor-pointer text-gray-400 hover:text-purple-600"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? '👁️‍🗨️' : '👁️'}
                    </span>
                  </div>
                </div>

                <div className="mb-5">
                  <label className="block mb-2 font-medium text-gray-600 text-sm">🔒 Conferma Password</label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Ripeti la password"
                      className="w-full p-3 pl-12 pr-12 border-2 border-gray-200 rounded-lg bg-gray-50 focus:border-purple-500 focus:bg-white focus:outline-none transition-all"
                    />
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">🔑</span>
                    <span 
                      className="absolute right-4 top-1/2 -translate-y-1/2 cursor-pointer text-gray-400 hover:text-purple-600"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? '👁️‍🗨️' : '👁️'}
                    </span>
                  </div>
                </div>

                <button
                  onClick={handleChangePassword}
                  disabled={loading}
                  className="w-full p-4 rounded-lg text-white font-semibold transition-all hover:shadow-lg disabled:opacity-60"
                  style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="animate-spin">⏳</span> Salvataggio...
                    </span>
                  ) : (
                    '💾 Cambia Password'
                  )}
                </button>
              </div>
            )}

            <div className="mt-6 pt-6 border-t text-center text-sm text-gray-600">
              <button onClick={onBack} className="text-purple-600 hover:text-purple-800 font-semibold">
                ← Torna al Login
              </button>
            </div>
          </div>
        </div>

        <div className="text-center mt-5 text-white text-xs opacity-80">
          © 2024 Timesheet Online - Tutti i diritti riservati
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
